import { CdkDrag, CdkDragDrop, CdkDragRelease, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Input,
  Output,
  DoCheck,
  Component,
  OnDestroy,
  EventEmitter,
  IterableDiffer,
  IterableDiffers,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnAbilityService } from '@lib/ability';
import { SnCamera } from '@lib/api/entities';
import { snZoomIn, snZoomOut } from '@lib/common/animations';
import { SnMenuItemConfig } from '@lib/ui/menu';

@Component({
  selector: 'sn-camera-list',
  templateUrl: './camera-list.component.html',
  styleUrls: ['./camera-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [snZoomIn, snZoomOut],
})
export class SnCameraListComponent implements DoCheck, OnDestroy {
  @Input()
  public cameras: SnCamera[] = [];

  @Input()
  public actions: SnMenuItemConfig<SnCamera>[] = [];

  @Input()
  public draggableList = false;

  @Input()
  public showLocation: boolean;

  @Input()
  public draggableToMap: boolean = false;

  @Output()
  public readonly focusedCameraChanged = new EventEmitter<SnCamera>();

  @Output()
  public readonly listDropped = new EventEmitter<void>();

  @Output()
  public readonly cameraReleased = new EventEmitter<{ source: CdkDrag; camera: SnCamera }>();

  public readonly canChange: boolean;

  private _focusedCamera: SnCamera;
  private readonly _camerasDiffer: IterableDiffer<SnCamera>;

  constructor(
    private readonly _iterableDiffers: IterableDiffers,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    ability: SnAbilityService,
  ) {
    this.canChange = ability.can({
      subject: 'maps/location',
      action: 'change',
    });
    this._camerasDiffer = this._iterableDiffers
      .find(this.cameras)
      .create((index, camera) => camera.id);
  }

  public ngDoCheck(): void {
    const changes = this._camerasDiffer.diff(this.cameras);

    if (changes) {
      this._changeDetectorRef.markForCheck();
    }
  }

  public ngOnDestroy(): void {
    this.focusedCameraChanged.complete();
    this.listDropped.complete();
  }

  public trackCamera(index: number, item: SnCamera): any {
    return item.id;
  }

  public onDropped(event: CdkDragDrop<SnCamera>): void {
    moveItemInArray(this.cameras, event.previousIndex, event.currentIndex);
    this.listDropped.next();
  }

  public onDragRelease(release: CdkDragRelease, camera: SnCamera): void {
    const source = release.source;
    this.cameraReleased.next({ source, camera });
    source.reset();
    source.element.nativeElement.style.position = 'initial';
  }

  public onDragStart($event: any): void {
    const element = $event.source.element.nativeElement;
    const box = element.getBoundingClientRect();
    element.style.top = `${box.top}px`;
    element.style.left = `${box.left}px`;
    element.style.position = 'fixed';
  }

  public toggleCamera(camera: SnCamera): void {
    this._focusedCamera =
      this._focusedCamera && this._focusedCamera.id !== camera.id ? camera : undefined;

    this.focusedCameraChanged.next(this._focusedCamera);
  }
}
